import { useCallback, useMemo } from "react";
import { useUIDSeed } from "react-uid";
import merge from "deepmerge";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { END } from "redux-saga";

import { SettingsActionTypes } from "@hmn/coolinarika-web-core/actions/settings";
import { Media } from "@hmn/coolinarika-web-core/controllers/MediaContext";
import { initializeGlobalProps, slugParser } from "@hmn/coolinarika-web-core/helpers";
import {
    getListElastic,
    getOneElastic,
    InfiniteLoadLoader,
    InfiniteLoadTrigger,
    lg,
    useBreakpoints,
    useImageUrl,
    useListElastic,
    useOneElastic,
    usePageUrl,
    useQueryState,
    useTasteMachine,
} from "@hmn/coolinarika-web-core/hooks";
import { storeWrapper } from "@hmn/coolinarika-web-core/store";

import { articleLabelVariants } from "@hmn/coolinarika-web-ui/components/Article/components/Card";
import {
    HeadingSectionHero,
    headingSectionHeroSizes,
} from "@hmn/coolinarika-web-ui/components/HeadingSection/components/Hero";
import { Select, selectVariants } from "@hmn/coolinarika-web-ui/components/Input/components/Select";
import { ListingSeo } from "@hmn/coolinarika-web-ui/components/Listing/components/Seo";
import { FooterSeparator } from "@hmn/coolinarika-web-ui/components/Misc/components";
import { NavigationTopBar } from "@hmn/coolinarika-web-ui/components/Navigation";
import { FeedItemController } from "@hmn/coolinarika-web-ui/controllers";

import { Motion } from "@web/components";
import { Column, Row } from "@web/layouts";

/**
 * Unsluggifies and capitalizes inspiration title.
 *
 * @param {*} title
 * @return {*}
 */
const prettifyTitle = (title = "") => {
    const result = title?.replace(/-/g, " ");
    return `${result.charAt(0).toUpperCase()}${result.substr(1)}`;
};

const AdSlot = dynamic(
    () => import("@hmn/coolinarika-web-ui/components/Promotion/components/AdSlot"),
    { ssr: false },
);

const selectSortOptions = [
    { value: JSON.stringify({ "metrics.interestingness": { order: "desc" } }), label: "Popularno" },
    { value: JSON.stringify({ published_at: { order: "desc" } }), label: "Najnovije" },
    { value: JSON.stringify({ published_at: { order: "asc" } }), label: "Najstarije" },
];

const getSelectOptionValue = (val, options) => options.find(({ value }) => value === val)?.value;

const Tag = ({ ...rest }) => {
    const uid = useUIDSeed();
    const url = usePageUrl();
    const { push, query } = useRouter();
    const [mediaQueryLarge] = useBreakpoints(lg);

    const { uuid, slug, id } = useMemo(() => slugParser(query));

    useTasteMachine({ type: "tag", itemId: uuid || id });

    const { data: inspiration } = useOneElastic({
        resource: "inspiracija",
        id: uuid || slug || id,
        enabled: !!(uuid || slug || id),
    });

    const isLandingPage = !!inspiration?.extended_attributes?.isLanding;
    const showBanners = !!inspiration?.extended_attributes?.showBanners;

    const [{ sort }, setQuery] = useQueryState({
        sort: isLandingPage ? selectSortOptions[1].value : selectSortOptions[0].value,
    });

    const { pages, ref, isFetching, isFetchingNextPage, hasNextPage } = useListElastic({
        resource: "inspiracija",
        id: uuid || slug || id,
        endpoint: "content",
        enabled: !!(uuid || slug || id),
        params: {
            sort,
            query: {
                showBanners,
            },
        },
        gtmData: {
            url,
            title: inspiration?.title,
        },
    });

    const showSpinner = (isFetching || isFetchingNextPage) && hasNextPage;

    const handleSortChange = useCallback(
        (event) => {
            setQuery((current) => ({
                ...current,
                sort: event?.target?.value,
            }));
        },
        [setQuery],
    );

    const sortOptionValue = useMemo(() => getSelectOptionValue(sort, selectSortOptions), [sort]);

    const handleButtonBackClick = useCallback(() => {
        push("/inspiracija");
    }, [push]);

    const mainImageUrl = useImageUrl(inspiration?.image?.id, "4:3", 1500, undefined, "jpg");

    const seoItem = useMemo(
        () => ({
            ...pages,
            title: inspiration?.title,
            description: inspiration?.description,
            url,
            mainImageUrl,
            articles: pages,
        }),
        [pages],
    );

    return (
        <Motion.Route {...rest}>
            <ListingSeo item={seoItem} />
            <>
                <Media greaterThan="md">
                    <AdSlot
                        adUnit="Tag_Desktop_Out_of_placement"
                        slotId="div-gpt-ad-1614172800617-0_17"
                        sizes={["fluid"]}
                        isInterstitial
                        programmaticId={17}
                    />
                </Media>
                <Media lessThan="lg">
                    <AdSlot
                        adUnit="Tag_Mobile_Out_of_placement"
                        slotId="div-gpt-ad-1614172288236-0_136"
                        sizes={["fluid"]}
                        isInterstitial
                        programmaticId={136}
                    />
                </Media>
            </>

            <NavigationTopBar />
            <HeadingSectionHero
                label={!isLandingPage ? "Inspiracija" : undefined}
                title={prettifyTitle(inspiration?.title || inspiration?.slug)}
                lead={inspiration?.description}
                // content={inspiration?.description}
                imageId={inspiration?.image?.id}
                onBackButtonClick={!isLandingPage ? handleButtonBackClick : undefined}
                backButtonIcon={mediaQueryLarge}
                hasShadow={!!inspiration?.image?.id}
                size={headingSectionHeroSizes.MEDIUM}
            />
            <Row variant="filters.row.offset">
                <Column variant="filters.column.select">
                    <Select
                        options={selectSortOptions}
                        variant={selectVariants.TERTIARY}
                        value={sortOptionValue}
                        onChange={handleSortChange}
                    />
                </Column>
            </Row>
            <Row variant="inspiration.main" disabled={!pages?.length}>
                <Column variant="grid.12.noPadding">
                    {!!pages?.length &&
                        pages?.map((inspirationPage, groupIndex) =>
                            inspirationPage?.map((item, index) => (
                                <FeedItemController
                                    gridVariant={groupIndex === 0 && index === 0 ? "2x2" : "1x1"}
                                    key={uid(item?.id || `${groupIndex}-${index}`)}
                                    index={index}
                                    groupIndex={groupIndex}
                                    item={item}
                                    type={item?.type}
                                    isFeedItem={false}
                                    customOverrideLabel={
                                        item?.type === "article"
                                            ? articleLabelVariants.SECONDARY
                                            : undefined
                                    }
                                />
                            )),
                        )}
                </Column>
            </Row>

            <InfiniteLoadTrigger ref={ref} />

            {showSpinner && <InfiniteLoadLoader />}

            <FooterSeparator />
        </Motion.Route>
    );
};

export const getServerSideProps = storeWrapper.getServerSideProps(async (ctx) => {
    const { params, query, store } = ctx;
    const { uuid, id, slug } = slugParser(params);
    const { data: inspirationData, dehydratedState: inspirationDehyratedState } =
        await getOneElastic({
            resource: "inspiracija",
            id: uuid || slug || id,
            enabled: !!(uuid || slug || id),
        });

    store.dispatch({ type: SettingsActionTypes.GET_SETTINGS });
    store.dispatch(END);
    await store.sagaTask.toPromise();

    // Return early 404
    if (!Object.keys(inspirationData || {}).length) {
        return {
            notFound: true,
        };
    }

    const isLandingPage = !!inspirationData?.extended_attributes?.isLanding;
    const showBanners = !!inspirationData?.extended_attributes?.showBanners;

    const defaultSort = isLandingPage ? selectSortOptions[1].value : selectSortOptions[0].value;

    const { dehydratedState: inspirationContentDehydratedState } = await getListElastic({
        resource: "inspiracija",
        id: uuid || slug || id,
        endpoint: "content",
        enabled: !!(uuid || slug || id),
        params: {
            sort: query?.sort || defaultSort,
            query: {
                showBanners,
            },
        },
    });

    const dehydratedState = merge.all([
        inspirationDehyratedState,
        inspirationContentDehydratedState,
    ]);

    const initialProps = await initializeGlobalProps({ ...ctx, dehydratedState });

    const returnProps = {
        props: {
            ...initialProps,
            id: uuid || id || slug,
        },
    };

    if (!slug || !uuid) {
        const { slug: redirectSlug, id: redirectUuid } = inspirationData || {};

        returnProps.redirect = {
            permanent: process.env.NODE_ENV === "production",
            destination: `/inspiracija/${redirectSlug || ""}${
                redirectUuid ? `-${redirectUuid}` : ""
            }`,
        };
    }

    return returnProps;
});

export default Tag;
